/*
 * ------------------------------------------------------------------------------------------
 * - External Imports -
 * ------------------------------------------------------------------------------------------
 */
import {
    faEyeSlash,
    faFileInvoiceDollar,
    faFirstAid,
    faLayerGroup,
    faPersonCircleCheck,
    faUser
} from '@fortawesome/pro-duotone-svg-icons';
import { faFileChartLine } from '@fortawesome/pro-duotone-svg-icons/faFileChartLine';
import { faCopyright } from '@fortawesome/pro-regular-svg-icons/faCopyright';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@material-ui/core';
import {
    CrfFormByCodesRoutes,
    ICrfFormMapping,
    IMenuItem,
    InstitutionsByCodesRoute,
    IOnlinePatientManagementLayoutProps,
    OnlinePatientManagementContext,
    OnlinePatientManagementLayout,
    PatientSummaryByStudyNumberRoute,
    RouteLoading,
    useIsMobile
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import {
    AdministerDepot,
    AdministerPharmacy,
    BatchManagement,
    DepotManagement,
    DrugDispensations,
    DrugManagementContext,
    DrugManagementPage,
    IDrugManagementContext
} from '@ngt/opms-drugmanagement';
import {
    InvoiceExportsErrorPage,
    InvoiceExportsPage,
    InvoiceForm,
    InvoiceManagementContext,
    InvoiceManagementPage,
    InvoicePatientPage,
    InvoiceRequisitionPage,
    InvoiceRequisitionRoute,
    InvoiceRequisitionsPage,
    InvoiceRoute,
} from '@ngt/opms-invoicemanagement';
import { RequestState } from '@ngt/request-utilities';
import React, { FC, Suspense, useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import * as Sentry from "@sentry/react";

/*
* ------------------------------------------------------------------------------------------
* - Internal Imports -
* ------------------------------------------------------------------------------------------
*/
import * as Dtos from './api/dtos';
import { AppSettings } from './AppSettings';
import ErrorPage, { ErrorTypeEnum } from './pages/error/ErrorPage';
import RegistrationFormPage from './pages/form/RegistrationFormPage';
import Registration from './pages/RegistrationPage';
import Reports from './pages/Reports';
import useSentryUser from './hooks/sentry/useSentryUser';
import ToggleFeedbackButton from './components/sentry/ToggleFeedbackButton';
/*
 * ------------------------------------------------------------------------------------------
 * - Constants -
 * ------------------------------------------------------------------------------------------
 */

const permissions: Permission[] = [
    Permission.OpmsAccess,
    Permission.DrugManagementView,
    Permission.DrugManagementAdminister,
    Permission.DrugManagementDispenseDrug,
    Permission.OpmsCrfUpdate,
    Permission.EcrfView,
    (Dtos.Permission.InvoiceView as Permission),
    (Dtos.Permission.InvoiceUpdate as Permission),
    (Dtos.Permission.ApproveInvoice as Permission),
    (Dtos.Permission.InvoiceExportView as Permission),
    (Dtos.Permission.InvoiceExportUpdate as Permission),
    (Dtos.Permission.InvoiceRequisitionView as Permission),
    (Dtos.Permission.InvoiceRequisitionUpdate as Permission),
    Permission.OpmsAdminister
];

const formMapping: Record<string, ICrfFormMapping> = {
    "registration-form": {
        components: <RegistrationFormPage />
    }
}

const SentryRoute = Sentry.withSentryRouting(Route);
const SentryInvoiceRoute = Sentry.withSentryRouting(InvoiceRoute);
const SentryInvoiceRequisitionRoute = Sentry.withSentryRouting(InvoiceRequisitionRoute);
const SentryInstitutionsByCodesRoute = Sentry.withSentryRouting(InstitutionsByCodesRoute);
const SentryPatientSummaryByStudyNumberRoute = Sentry.withSentryRouting(PatientSummaryByStudyNumberRoute);
const SentryCrfFormByCodesRoutes = Sentry.withSentryRouting(CrfFormByCodesRoutes);

/*
 * ------------------------------------------------------------------------------------------
 * - Exports -
 * ------------------------------------------------------------------------------------------
 */
export const App: FC = () => {
    useSentryUser();
    const onlinePatientManagement = React.useContext(OnlinePatientManagementContext);
    const drugManagementContext = React.useContext<IDrugManagementContext>(DrugManagementContext);
    const drugManagementBaseUrl = drugManagementContext?.baseUrl;

    const invoiceManagementContext = React.useContext(InvoiceManagementContext);
    const invoiceManagementUrl = invoiceManagementContext?.managementUrl;
    const invoiceExportsUrl = invoiceManagementContext?.exportsUrl;
    const invoiceRequisitionsUrl = invoiceManagementContext?.requisitionsUrl;

    //const invoiceManagementSummaryUrl = invoiceManagementContext?.invoiceUrl;

    // const isInvoiceRoute = React.useMemo(() => {
    //     return document.location.pathname.startsWith(invoiceManagementBaseUrl) ||
    //         document.location.pathname.startsWith(invoiceManagementSummaryUrl)
    // }, [invoiceManagementBaseUrl, document.location.pathname]);

    const [
        [
            canAccessOpms,
            canViewDrugManagement,
            canAdministerDrugManagement,
            canDispenseDrug,
            canUpdateForm,
            canViewCrf,
            canViewInvoice,
            canUpdateInvoice,
            canApproveInvoice,
            canViewInvoiceExport,
            canUpdateInvoiceExport,
            canViewInvoiceRequisition,
            canUpdateInvoiceRequisition,
            canAdministerOpms
        ],
        permissionLoadState
    ] = usePermissionsByIds(permissions, null, null, null, null, true);

    const menuItems: IMenuItem[] = React.useMemo(() => {
        let items: IMenuItem[] = [];

        if (canUpdateForm) {
            items.push(
                {
                    url: '/registration',
                    label: 'Registration',
                    external: false,
                    newTab: false,
                    icon: faUser as any
                }
            );
        }

        if (canViewDrugManagement) {
            items.push({
                url: drugManagementBaseUrl,
                label: 'Drug Management',
                external: false,
                newTab: false,
                icon: faFirstAid as any
            });
        }

        if (AppSettings.invoicesEnabled && (canViewInvoice)) {
            items.push({
                url: invoiceManagementUrl,
                label: 'Invoices',
                external: false,
                newTab: false,
                icon: faFileInvoiceDollar as any,
                sidebarItems: [
                    {
                        url: invoiceManagementUrl,
                        label: 'Invoice Management',
                        external: false,
                        newTab: false,
                        icon: faPersonCircleCheck as any
                    },
                    ...(canViewInvoiceExport ? [{
                        url: invoiceExportsUrl,
                        label: 'Invoice Summary',
                        external: false,
                        newTab: false,
                        icon: faFileInvoiceDollar as any
                    }] : []),
                    ...(canViewInvoiceRequisition ? [{
                        url: invoiceRequisitionsUrl,
                        label: 'Invoice Requisitions',
                        external: false,
                        newTab: false,
                        icon: faLayerGroup as any
                    }] : [])
                ]
            });
        }

        items.push({
            url: '/reports',
            label: 'Reports',
            external: false,
            newTab: false,
            icon: faFileChartLine as any
        });

        items.push(
            {
                url: AppSettings.redactUrl,
                label: 'Redact',
                external: true,
                newTab: true,
                icon: faEyeSlash as any
            }
        );

        return items;
    }, [AppSettings.invoicesEnabled, canViewCrf, canUpdateForm, canViewInvoiceExport, canViewInvoiceRequisition, canViewDrugManagement, drugManagementBaseUrl, invoiceManagementUrl, invoiceExportsUrl]);

    const InvalidPermission = (permissionLoadState.state === RequestState.Cancel ||
        permissionLoadState.state === RequestState.Failure ||
        permissionLoadState.state === RequestState.Success) &&
    !canAccessOpms &&
    !canViewInvoice;

    const isMobile = useIsMobile();

    const additionalHeaderComponents: IOnlinePatientManagementLayoutProps['additionalHeaderComponents'] = useMemo(() => {
        if (!AppSettings.sentry.enableReportABug) {
            return undefined;
        }

        return {
            beforeUserIcon: [
                ToggleFeedbackButton
            ]
        }
    }, [])

    if (permissionLoadState.state === RequestState.None || permissionLoadState.state === RequestState.Pending) {
        return (
            <RouteLoading />
        );
    }

    return (
        <OnlinePatientManagementLayout
            menuItems={menuItems}
            additionalHeaderComponents={additionalHeaderComponents}
            footer={
                isMobile ?
                    undefined :
                    <Typography variant="caption">
                        Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                    </Typography>
            }
        >
            {
                InvalidPermission ?
                <ErrorPage errorType={ErrorTypeEnum.e403} /> :
                <Suspense fallback={<RouteLoading />}>
                    <Switch>
                        <SentryCrfFormByCodesRoutes
                            path={[
                                `/registration/:${onlinePatientManagement.routeParameters.institutionCode}/add-participant`
                            ]}
                            eventDefinitionCode="registration"
                            eventRepeat={1}
                            formDefinitionCode="registration-form"
                            formRepeat={1}
                            createPatient
                            resolveBeforeLoad
                            formMapping={formMapping}
                        />

                        <SentryCrfFormByCodesRoutes
                            path={[
                                `/registration/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventDefinitionCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/:${onlinePatientManagement.routeParameters.formDefinitionCode}/:${onlinePatientManagement.routeParameters.formRepeat}/:mode`,
                                `/registration/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventDefinitionCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/:${onlinePatientManagement.routeParameters.formDefinitionCode}/:${onlinePatientManagement.routeParameters.formRepeat}`
                            ]}
                            resolveBeforeLoad
                            formMapping={formMapping}
                        />

                        <SentryRoute
                            path={[
                                `${drugManagementBaseUrl}/batches`
                            ]}
                        >
                            <BatchManagement />
                        </SentryRoute>
                        <SentryRoute
                            path={[
                                `${drugManagementBaseUrl}/depots`
                            ]}
                        >
                            <DepotManagement />
                        </SentryRoute>
                        <SentryRoute
                            path={[
                                `${drugManagementBaseUrl}/depot/:depotId`
                            ]}>
                            <AdministerDepot />
                        </SentryRoute>
                        <SentryRoute
                            path={[
                                `${drugManagementBaseUrl}/pharmacy/:pharmacyId`
                            ]}
                        >
                            <AdministerPharmacy
                                canAdministerDrugManagement={canAdministerDrugManagement}
                                canAdministerPharmacyStock={canAdministerDrugManagement}
                            />
                        </SentryRoute>
                        <SentryPatientSummaryByStudyNumberRoute
                            path={[
                                `/drug-management/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/drug-dispensation`
                            ]}
                        >
                            <DrugDispensations
                                canAdministerDrugManagement={canAdministerDrugManagement}
                                canDispenseDrug={canDispenseDrug}
                            />
                        </SentryPatientSummaryByStudyNumberRoute>
                        <SentryRoute
                            path={[
                                `${drugManagementBaseUrl}/:${onlinePatientManagement.routeParameters.institutionCode}`,
                                `${drugManagementBaseUrl}`
                            ]}>
                            <DrugManagementPage
                                canAdministerDrugManagement={canAdministerDrugManagement}
                                canDispenseDrug={canDispenseDrug}
                                canViewDrugManagement={canViewDrugManagement}
                            />
                        </SentryRoute>

                        <SentryInstitutionsByCodesRoute
                            path={[
                                `/registration/:${onlinePatientManagement.routeParameters.institutionCode}`,
                                '/registration'
                            ]}
                        >
                            <Registration canAdministerOpms={canAdministerOpms} />
                        </SentryInstitutionsByCodesRoute>

                        <SentryRoute
                            path={[
                                `${drugManagementBaseUrl}/:${onlinePatientManagement.routeParameters.institutionCode}`,
                                `${drugManagementBaseUrl}`
                            ]}>
                            <DrugManagementPage
                                canAdministerDrugManagement={canAdministerDrugManagement}
                                canDispenseDrug={canDispenseDrug}
                                canViewDrugManagement={canViewDrugManagement}
                            />
                        </SentryRoute>

                        <SentryInvoiceRoute
                            path={[
                                `${invoiceManagementContext?.managementUrl}/error`
                            ]}
                        >
                            <InvoiceExportsErrorPage />
                        </SentryInvoiceRoute>

                        <SentryInvoiceRequisitionRoute
                            path={[
                                `${invoiceRequisitionsUrl}/:${invoiceManagementContext.routeParameters.institutionCode}/:${invoiceManagementContext.routeParameters.requisitionId}`,
                            ]}
                        >
                            <InvoiceRequisitionPage 
                                canViewInvoiceRequisition={canViewInvoiceRequisition}
                                canCreateInvoiceRequisition={canUpdateInvoiceRequisition}
                            />
                        </SentryInvoiceRequisitionRoute>

                        <SentryInvoiceRoute
                            path={[
                                `${invoiceManagementUrl}/:${invoiceManagementContext.routeParameters.institutionCode}/:${invoiceManagementContext.routeParameters.patientStudyNumber}/new`,
                            ]}
                        >
                            <InvoiceForm
                                canViewInvoiceManagement={canViewInvoice}
                                canUpdateInvoiceManagement={canUpdateForm || canUpdateInvoice}
                                canAdministerInvoiceManagement={canApproveInvoice}
                                insert={true}
                            />
                        </SentryInvoiceRoute>

                        <SentryInvoiceRoute
                            path={[
                                `${invoiceManagementUrl}/:${invoiceManagementContext.routeParameters.institutionCode}/:${invoiceManagementContext.routeParameters.patientStudyNumber}/:${invoiceManagementContext.routeParameters.invoiceId}`,
                            ]}
                        >
                            <InvoiceForm
                                canViewInvoiceManagement={canViewInvoice}
                                canUpdateInvoiceManagement={canUpdateInvoice}
                                canAdministerInvoiceManagement={canApproveInvoice}
                                insert={false}
                            />
                        </SentryInvoiceRoute>

                        <SentryInvoiceRoute
                            path={[
                                `${invoiceExportsUrl}`
                            ]}
                        >
                            <InvoiceExportsPage
                                canViewInvoiceManagement={canViewInvoiceExport}
                                canUpdateInvoiceManagement={canUpdateInvoiceExport}
                            />
                        </SentryInvoiceRoute>

                        <SentryInvoiceRequisitionRoute
                            path={[
                                `${invoiceRequisitionsUrl}/:${invoiceManagementContext.routeParameters.institutionCode}`,
                                `${invoiceRequisitionsUrl}`
                            ]}
                        >
                            <InvoiceRequisitionsPage 
                                canViewInvoiceRequisitions={canViewInvoiceRequisition}
                                canCreateInvoiceRequisitions={canUpdateInvoiceRequisition}
                            />
                        </SentryInvoiceRequisitionRoute>

                        <SentryInvoiceRoute
                            path={[
                                `${invoiceManagementUrl}/:${invoiceManagementContext.routeParameters.institutionCode}/:${invoiceManagementContext.routeParameters.patientStudyNumber}`,
                            ]}
                        >
                            <InvoicePatientPage
                                canViewInvoiceManagement={canViewInvoice}
                            />
                        </SentryInvoiceRoute>

                        <SentryInvoiceRoute
                            path={[
                                `${invoiceManagementUrl}/:${invoiceManagementContext.routeParameters.institutionCode}`,
                                `${invoiceManagementUrl}`
                            ]}
                        >
                            <InvoiceManagementPage
                                canViewInvoiceManagement={canViewInvoice}
                                //canUpdateInvoiceManagement={canUpdateInvoice}
                            />
                        </SentryInvoiceRoute>

                        <SentryInstitutionsByCodesRoute
                            path={[
                                '/reports'
                            ]}
                        >
                            <Reports />
                        </SentryInstitutionsByCodesRoute>

                        {
                            !!canUpdateForm && <Redirect path="*" to="/registration" />
                        }

                        {
                            !!canViewDrugManagement && <Redirect path="*" to="/drug-management" />
                        }

                        {
                            !!canViewInvoice && <Redirect path="*" to="/invoice-management" />
                        }
                    </Switch>
                </Suspense>
            }
        </OnlinePatientManagementLayout>
    )
}

export default App
